import { useEffect, useState } from "react";
import { supabase } from "@vinoga/db";
import { Boxes, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

const sectionClass = "bg-card border border-border rounded-lg p-5 space-y-4";
const inputClass =
  "w-full bg-card border border-border rounded-lg px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary placeholder:text-muted-foreground";
const labelClass = "font-body text-xs font-medium text-muted-foreground mb-1 block";

// Non-secret config persisted to moneo_settings.config (jsonb). Secrets
// (MONEO_API_KEY / MONEO_COMPANY_ID) live in env vars, never here.
type MoneoConfig = {
  location?: string;
  pricelist?: string;
  retailCustCode?: string;
};

type Setting = {
  id: string;
  active: boolean;
  display_name: string;
  config: MoneoConfig;
  updated_at: string;
};

export function MoneoSettings() {
  const { toast } = useToast();
  const t = useT();

  const [setting, setSetting] = useState<Setting | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("moneo_settings")
        .select("*")
        .limit(1);
      if (cancelled) return;
      if (error) {
        toast({
          title: t("moneoSettings.errorLoading", "Error loading Moneo settings"),
          description: error.message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      setSetting(((data ?? [])[0] ?? null) as Setting | null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [t, toast]);

  const updateConfig = (patch: Partial<MoneoConfig>) => {
    setSetting((prev) =>
      prev ? { ...prev, config: { ...prev.config, ...patch } } : prev,
    );
  };

  const save = async () => {
    if (!setting) return;
    setSaving(true);
    // Strip blank keys so config stays minimal.
    const config: MoneoConfig = {};
    if (setting.config.location?.trim()) config.location = setting.config.location.trim();
    if (setting.config.pricelist?.trim()) config.pricelist = setting.config.pricelist.trim();
    if (setting.config.retailCustCode?.trim())
      config.retailCustCode = setting.config.retailCustCode.trim();

    const { error } = await supabase
      .from("moneo_settings")
      .update({ active: setting.active, config })
      .eq("id", setting.id);
    if (error) {
      toast({
        title: t("common.saveFailed", "Save failed"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: t("moneoSettings.saved", "Moneo settings saved") });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="p-4 font-body text-sm text-muted-foreground">
        {t("common.loading", "Loading...")}
      </div>
    );
  }
  if (!setting) {
    return (
      <div className="p-4 font-body text-sm text-muted-foreground">
        {t("moneoSettings.empty", "No Moneo settings row found.")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-xl font-bold text-foreground">
          {t("moneoSettings.title", "Moneo ERP")}
        </h1>
        <p className="font-body text-xs text-muted-foreground">
          {t(
            "moneoSettings.subtitle",
            "When enabled, each paid order pushes a prepayment invoice to Moneo. Off by default — turn on to test live. API credentials (MONEO_API_KEY / MONEO_COMPANY_ID) live in env vars (Railway), not here.",
          )}
        </p>
      </div>

      <section className={sectionClass}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Boxes size={18} className="text-muted-foreground" />
            <div>
              <h2 className="font-display text-sm font-bold text-foreground">
                {setting.display_name}
              </h2>
              <p className="font-mono text-xs text-muted-foreground">moneo</p>
            </div>
            {setting.active ? (
              <Badge variant="success">{t("moneoSettings.statusOn", "Enabled")}</Badge>
            ) : (
              <Badge variant="secondary">{t("moneoSettings.statusOff", "Disabled")}</Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={setting.active}
              onCheckedChange={(v) =>
                setSetting((p) => (p ? { ...p, active: v } : p))
              }
            />
            <Button size="sm" onClick={save} disabled={saving}>
              <Save size={14} />
              {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 border-t border-border pt-3 sm:grid-cols-3">
          <div>
            <label className={labelClass}>
              {t("moneoSettings.location", "Fulfilment warehouse")}
            </label>
            <input
              value={setting.config.location ?? ""}
              onChange={(e) => updateConfig({ location: e.target.value })}
              placeholder="BAZ"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              {t("moneoSettings.pricelist", "Price list")}
            </label>
            <input
              value={setting.config.pricelist ?? ""}
              onChange={(e) => updateConfig({ pricelist: e.target.value })}
              placeholder="MAZ"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              {t("moneoSettings.custCode", "Retail contact code")}
            </label>
            <input
              value={setting.config.retailCustCode ?? ""}
              onChange={(e) => updateConfig({ retailCustCode: e.target.value })}
              placeholder="29234"
              className={inputClass}
            />
          </div>
        </div>

        <div className="space-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
          <div>
            <span className="font-medium">
              {t("moneoSettings.credentials", "Credentials env vars:")}
            </span>{" "}
            <span className="font-mono">MONEO_API_KEY / MONEO_COMPANY_ID</span>
          </div>
          <div>
            {t(
              "moneoSettings.hint",
              "Creates a prepayment invoice (createddatasource=2) on each paid order. Line items use each product's SKU as the Moneo item code.",
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
